/**
 * Derive the create-dedup token for an occurrence SPLIT OFF a recurring series.
 *
 * Dragging or editing one occurrence out of a series creates a NEW document
 * beside the original. The calendar builds that document by copying the parent —
 * which, before this, copied the parent's `clientIdempotencyKey` too.
 *
 * That key is a CREATE-DEDUP token, and the backend honours it hard: `user-messages`
 * `before.create` -> `dedupeByClientIdempotencyKey` looks the key up, finds the
 * parent, sets `context.result` to it and performs NO insert. The request still
 * answers 201 with a perfectly valid message document, so the client cannot tell
 * the create was discarded. The parent meanwhile gets its EXDATE, so the dragged
 * occurrence leaves the series and never reappears anywhere: the message the
 * customer moved simply vanishes.
 *
 * The backend already had to learn this for the recurring successor chain
 * (`stripInheritedIdentity`, BE#176) and the multi-recipient fan-out learned it on
 * the client (`derivePerRecipientIdempotencyKey`, FE#204). This is the same lesson
 * for the third minting site: a new document needs its own key.
 *
 * The key stays DERIVED rather than random so the token remains idempotent for its
 * own purpose — re-submitting the same drag (a double-click, a retried request)
 * dedupes onto the one document it already made, instead of leaving two copies at
 * the new date. It is keyed on the SOURCE occurrence, not the destination: two
 * different occurrences dropped onto the same day are two different messages, and
 * keying on the destination would silently collapse them into one — the very bug
 * this exists to prevent.
 */
import { newClientIdempotencyKey } from '@common/utils/client-idempotency-key';

/**
 * Mongo indexes the key, and repeated splits append, so a pathologically
 * re-dragged message could otherwise grow a key past the index limit. Past this
 * bound we trade cross-retry idempotency for a key that is certain to be
 * insertable and certain not to collide.
 */
const MAX_DERIVED_KEY_LENGTH = 512;

export function deriveSplitOccurrenceIdempotencyKey(
  parentKey: string | undefined | null,
  occurrence: Date | string | number | undefined | null,
  /**
   * Where the occurrence was dropped, when it was moved somewhere.
   *
   * Folded in so a retry that changes its mind is NOT deduped onto the document a
   * previous attempt already created. `applyRecurringEditPlan` creates the
   * replacement before patching the original, so "create landed, patch did not" is
   * a state the ordering itself can produce: the occurrence is still in the series,
   * the user drags it again — possibly somewhere else — and with a source-only key
   * that second drag silently resolves to the FIRST destination while the calendar
   * reports the second. Landing quietly on the wrong date is the worst outcome
   * available here; a visible leftover the user can see and delete is not.
   *
   * A genuine retry of the SAME move keeps the same key and still dedups, and two
   * different occurrences dropped on one day still differ by their source.
   *
   * Omitted by callers that split nothing off (a deletion has no destination).
   */
  destination?: Date | string | number | null,
  gen: () => string = newClientIdempotencyKey,
): string | undefined {
  // No parent key means no collision to avoid: the sparse index ignores keyless
  // creates, so leaving it unset preserves the existing behaviour exactly.
  if (!parentKey) return undefined;

  const at = occurrence == null ? NaN : new Date(occurrence as any).getTime();
  // Without a usable occurrence there is nothing stable to derive from, but the
  // parent's key must still not be reused — a fresh key is the safe direction.
  if (!Number.isFinite(at)) return gen();

  const to = destination == null ? NaN : new Date(destination as any).getTime();
  const derived = Number.isFinite(to)
    ? `${parentKey}:occ:${at}:to:${to}`
    : `${parentKey}:occ:${at}`;
  return derived.length > MAX_DERIVED_KEY_LENGTH ? gen() : derived;
}
